import type { ICreditCard } from "@/data/model/CreditCard";
import { Misc } from "@/data/util/Misc";

import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export class CreditCardController extends BaseController {
    public onGoBack?: { () };

    private $methodList: JQuery;
    userCreditCardsService: UserCreditCardsService;

    constructor() {
        super("creditcard");

        this.userCreditCardsService = new UserCreditCardsService();
        const hdr = $('<div class="head"><div class="title">' + trans.creditCard.title + "</div></div>").appendTo(
            this.$view
        );
        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        this.$methodList = $('<div class="cards scrollable" />').appendTo(this.$view);
    }

    protected onBecomeVisible() {
        this.updateCards();
        UserCreditCardsService.onCreditCardsChanged.add(this.updateCards, this);
    }

    protected onBecomeInvisible() {
        UserCreditCardsService.onCreditCardsChanged.remove(this.updateCards, this);
    }

    private updateCards() {
        this.$methodList.empty();
        const profile = TastierClient.identityContext.getProfile()!;

        for (const i in profile.creditCards) {
            const card = profile.creditCards[i];
            this.addRowForCard(card);
        }
    }

    private addRowForCard(card: ICreditCard, prepend?: boolean) {
        const row = $('<div class="row" />').data("model", card);

        if (prepend !== undefined && prepend === true) row.prependTo(this.$methodList);
        else row.appendTo(this.$methodList);

        this.renderCard(card).appendTo(row);

        $('<div class="remover"><i class="fal fa-trash-alt"></i></div>')
            .appendTo(row)
            .click((e) => {
                Popup.show(
                    trans.creditCard.confirmDelete,
                    async () => {
                        const remover = $(e.currentTarget);
                        remover.append('<div class="loader" />').find("> .fal").hide();

                        const parent = remover.parents(".row");

                        try {
                            await this.userCreditCardsService.removeCreditCard(parent.data("model").id);

                            parent
                                .addClass("removed")
                                .delay(1000)
                                .queue((de) => {
                                    parent.remove();
                                    de();
                                });
                        } catch (ex) {
                            logError(ex);
                            remover.find("> .loader").remove();
                            remover.find("> .fal").show();
                            Popup.show(trans.creditCard.failedRemove);
                        }
                    },
                    trans.creditCard.deleteBtn,
                    trans.general.cancel
                );
            });
    }

    private renderCard(card: ICreditCard): JQuery {
        const pm = $('<div class="card" />');

        pm.append('<div class="brandicon ' + card.brand!.toLowerCase() + '" />');
        pm.append(
            '<div class="number"><span class="bullets">••••</span><span class="bullets">••••</span><span class="bullets">••••</span><span class="digits">' +
                card.info +
                "</span></div>"
        );

        if (card.expiryDate !== undefined)
            pm.append(
                '<div class="expiry">' +
                    Misc.padDigits(card.expiryDate.getMonth() + 1, 2) +
                    "/" +
                    card.expiryDate.getFullYear().toString().substr(2) +
                    "</div>"
            );

        if (Helper.creditCardIsExpired(card)) pm.append('<div class="expired">' + trans.creditCard.expired + "</div>");

        if (card.holder !== undefined) pm.append('<div class="holder">' + card.holder + "</div>");

        return pm;
    }
}
